// React
import { useState, useEffect, useMemo } from "react";

// React-Redux
import { useSelector } from "react-redux";

// React-Query
import { useQuery, useQueryClient } from "@tanstack/react-query";

// API's
import { getUserPayPlansHistory } from "@/PayPlans/api/users/queries";

// Context
import { gql, useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Layouts
import AppSideBar from "@/Layouts/components/SideBars/AppSideBar";
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";

// Custom Components
import PayPlansModel from "@/PayPlans/models/MyPayPlans";

// Images
import empty from "@/assets/images/commons/empty.png";
import { useTermsAndConditionEdition } from "@/PayPlans/hooks/useTermsAndCondition";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import { normalizePositions } from "@/Common/helpers/Positions";
//Subscriptions
import { getAssignmentsSubscriptions } from "./subscriptions";

const checkUpComing = (payPlans) => {
	let upcoming = false;
	payPlans.forEach((element) => {
		if (element.upcoming) {
			upcoming = true;
		}
	});
	return upcoming;
};

export default function MyPayPlans() {
	const [selected, setSelected] = useState(null);
	const userLogged = useSelector((state) => state.user.user);
	const position = userLogged?.position || "";
	const location = useSelector((state) => state.location.location);
	const queryClient = useQueryClient();
	const [treeData, setTreeData] = useState([]);
	const { clearCache } = useCacheCleaner();
	/******************************************** SUBSCRIPTIONS ***************************************************/
	const { getSubscription, addSubscription, clearSubscriptionContext } = useSubscriptions();
	const { payPlanAvailability } = useTermsAndConditionEdition(position);
	const myPayPlansSubscription = getSubscription(["myPayPlanAssignment", selected?._id]);
	myPayPlansSubscription?.subscriptions?.onCreatePayPlanAssignment?.setCallback((_response) => {
		queryClient?.invalidateQueries(["myPayPlansSideBar", userLogged?._id, location?.locationId]);
	});

	myPayPlansSubscription?.subscriptions?.onUpdateUserPayPlanAssignment?.setCallback((_response) => {
		queryClient?.invalidateQueries(["myPayPlansSideBar", userLogged?._id, location?.locationId]);
	});

	myPayPlansSubscription?.subscriptions?.onRemoveUserPayPlanAssignment?.setCallback((_response) => {
		queryClient?.invalidateQueries(["myPayPlansSideBar", userLogged?._id, location?.locationId]);
	});

	/************************************************ QUERIES ******************************************************/
	const { data: tree, isLoading } = useQuery({
		queryKey: ["myPayPlansSideBar", userLogged?._id, location?.locationId],
		queryFn: async () => {
			const result = await getUserPayPlansHistory(userLogged?._id, location?.locationId);
			const { currentPayPlanAssignment, upcomingPayPlanAssignment, payPlanAssignmentHistory = [] } = result || {};

			const filteredCurrentPayPlanAssignment = currentPayPlanAssignment?.locationId === location?.locationId ? currentPayPlanAssignment : null;
			if (filteredCurrentPayPlanAssignment) {
				const itemSelected = {
					...filteredCurrentPayPlanAssignment,
					_id: filteredCurrentPayPlanAssignment?.planId?._id ?? "",
					title: filteredCurrentPayPlanAssignment?.planId?.name ?? "",
				};

				setSelected(itemSelected);
			}
			const filteredUpcomingPayPlanAssignment = upcomingPayPlanAssignment?.locationId === location?.locationId ? upcomingPayPlanAssignment : null;
			const filteredPayPlanAssignmentHistory = payPlanAssignmentHistory.filter(plan => plan.locationId === location?.locationId);
			return getMappedMainTree(filteredCurrentPayPlanAssignment, filteredUpcomingPayPlanAssignment, filteredPayPlanAssignmentHistory);
		},
		staleTime: Infinity,
		enabled: !!userLogged?._id && !!location?.locationId,
	});

	const getMappedMainTree = (currentPayPlanAssignment, upcomingPayPlanAssignment, payPlanAssignmentHistory) => {
		//PayPlanAssignmentHistory
		const previouslyAssigned = [];
		payPlanAssignmentHistory?.forEach((element) => {
			const elementTree = {
				...element,
				title: element.planId.name,
				_id: element.planId._id,
			};
			previouslyAssigned.push(elementTree);
		});
		//UpcomingPayPlanAssignment
		const upCommings = [];
		if (upcomingPayPlanAssignment) {
			upCommings.push({
				...upcomingPayPlanAssignment,
				upcoming: true,
				_id: upcomingPayPlanAssignment?.planId?._id ?? "",
				title: upcomingPayPlanAssignment?.planId?.name ?? "",
			});
		}
		//CurrentPayPlanAssignment
		const currentlyAssigned = [];
		if (currentPayPlanAssignment) {
			currentlyAssigned.push({
				...currentPayPlanAssignment,
				_id: currentPayPlanAssignment?.planId?._id ?? "",
				title: currentPayPlanAssignment?.planId?.name ?? "",
			});
		}
		return [
			{
				_id: "upCommings",
				title: "Upcomming Assignment",
				bold: true,
				root: true,
				disabled: upCommings.length === 0,
				list: upCommings,
			},
			{
				_id: "currentlyAssigned",
				title: "Currently Assigned",
				bold: true,
				root: true,
				disabled: currentlyAssigned.length === 0,
				list: currentlyAssigned,
			},
			{
				_id: "previouslyAssigned",
				title: "Previously Assigned",
				bold: true,
				root: true,
				disabled: previouslyAssigned.length === 0,
				list: previouslyAssigned,
			},
		];
	};

	useEffect(() => {
		if (tree) {
			setTreeData(tree);
		}

		return () => {
			setTreeData([]);
		};
	}, [tree]);

	const sideBar = useMemo(() => {
		const handleSelection = (selection) => {
			if (selection?._id !== selected?._id) {
				setSelected(selection);
			}
		};
		return (
			<AppSideBar
				searchProps={{
					loading: isLoading,
				}}
				sideBarProps={{
					tree: treeData || [],
					selected: selected?._id ?? "",
					loading: isLoading,
					disabled: isLoading,
					onSelectAction: handleSelection,
				}}
			/>
		);
	}, [isLoading, selected?._id, treeData]);

	useEffect(() => {
		return () => {
			setSelected(null);
			clearSubscriptionContext();
		};
	}, [location, clearSubscriptionContext]);

	useEffect(() => {
		addSubscription(["myPayPlanAssignment", selected?._id], getAssignmentsSubscriptions());
	}, [addSubscription, selected?._id]);

	useEffect(() => {
		const normalizedPosition = normalizePositions(position);
		if (payPlanAvailability) {
			const { pdfAvailability, _id, position: positionAvail, locationId } = payPlanAvailability;
			if (locationId === location.locationId && positionAvail === normalizedPosition) {
				const disabledDataTree = (treeNode) => {
					let pdfAvailabilityAux = treeNode.pdfAvailability;
					if (!_id || _id === treeNode._id) {
						pdfAvailabilityAux = pdfAvailability;
					}
					return {
						...treeNode,
						disabled: !pdfAvailabilityAux,
						pdfAvailability: pdfAvailabilityAux,
					};
				};

				clearCache(["htmlPayPlanView", selected?._id, location]);
				setTreeData((previousDataTree) => previousDataTree.map(disabledDataTree));
				setSelected(null);
			}
		}
	}, [payPlanAvailability]);

	return (
		<BodyLayer fixedContent={sideBar}>
			{selected ? (
				<PayPlansModel
					position={position}
					selected={selected}
					locationId={location?.locationId}
					treeData={treeData}
					loading={isLoading}
				/>
			) : (
				<EmptyBody
					image={empty}
					title={"No Pay Plans Assigned."}
					bodyHeight={"570px"}
					sx={{
						boxShadow: "none !important",
					}}
				/>
			)}
		</BodyLayer>
	);
}
